import { Pencil, CheckCircle, XCircle } from 'lucide-react';
import {
  FIELDS_BY_DOC_TYPE,
  VENDOR_FIELDS,
  BILL_CREATION_FIELDS,
  OnboardingConfig,
  WizardStepId,
  DocumentType,
} from '@/types/onboarding';
import { getErpProviderLabel } from '@api/quickbooksAuth';

// ===== Helper: human-readable labels for document types =====

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  purchase_order: 'Purchase Order',
  receipt: 'Receipt',
  invoice: 'Invoice',
};

// ===== SectionCard =====

interface SectionCardProps {
  title: string;
  stepId: WizardStepId;
  onEdit: (stepId: WizardStepId) => void;
  children: React.ReactNode;
}

const SectionCard = ({ title, stepId, onEdit, children }: SectionCardProps) => (
  <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {title}
      </h3>
      <button
        type="button"
        onClick={() => onEdit(stepId)}
        className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
      >
        <Pencil className="h-3 w-3" />
        Edit
      </button>
    </div>
    {children}
  </div>
);

// ===== FieldPills =====

interface FieldPillsProps {
  keys: string[];
  allFields: { key: string; label: string }[];
}

const FieldPills = ({ keys, allFields }: FieldPillsProps) => {
  const fieldMap = new Map(allFields.map((f) => [f.key, f.label]));

  return (
    <div className="flex flex-wrap gap-1.5">
      {keys.map((key) => (
        <span
          key={key}
          className="px-2 py-0.5 text-xs rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
        >
          {fieldMap.get(key) ?? key}
        </span>
      ))}
    </div>
  );
};

// ===== ReviewStep =====

interface ReviewStepProps {
  extractionFields: Record<string, string[]>;
  erpConfig: OnboardingConfig['erp_integration'] | undefined;
  mailboxConfig: OnboardingConfig['mailbox_integration'] | undefined;
  vendorFields: string[];
  billFields: string[];
  onEditStep: (stepId: WizardStepId) => void;
}

export const ReviewStep = ({
  extractionFields,
  erpConfig,
  mailboxConfig,
  vendorFields,
  billFields,
  onEditStep,
}: ReviewStepProps) => {
  return (
    <div className="space-y-6">
      {/* Heading */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          Review &amp; Confirm
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Review your configuration before completing setup.
        </p>
      </div>

      {/* 1. Document Extraction Fields */}
      <SectionCard
        title="Document Extraction Fields"
        stepId="extraction_fields"
        onEdit={onEditStep}
      >
        <div className="space-y-3">
          {(Object.keys(FIELDS_BY_DOC_TYPE) as DocumentType[]).map(
            (docType) => {
              const selectedKeys = extractionFields[docType] ?? [];
              const allFields = FIELDS_BY_DOC_TYPE[docType];
              return (
                <div key={docType}>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {DOC_TYPE_LABELS[docType]}{' '}
                    <span className="text-gray-400 dark:text-gray-500">
                      ({selectedKeys.length} fields)
                    </span>
                  </p>
                  <FieldPills keys={selectedKeys} allFields={allFields} />
                </div>
              );
            }
          )}
        </div>
      </SectionCard>

      {/* 2. ERP Integration */}
      <SectionCard
        title="ERP Integration"
        stepId="erp_integration"
        onEdit={onEditStep}
      >
        {erpConfig ? (
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-gray-900 dark:text-gray-100">
              {getErpProviderLabel(erpConfig.erp_type)} configured{' '}
              <span className="text-gray-500 dark:text-gray-400">
                ({erpConfig.is_connected ? 'Connected' : 'Not connected'})
              </span>
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <XCircle className="h-4 w-4 text-gray-400" />
            <span className="text-gray-400">Skipped</span>
          </div>
        )}
      </SectionCard>

      {/* 3. Mailbox Integration */}
      <SectionCard
        title="Mailbox Integration"
        stepId="mailbox_integration"
        onEdit={onEditStep}
      >
        {mailboxConfig ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-gray-900 dark:text-gray-100">
                Microsoft connected
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 ml-6 space-y-0.5">
              <p>
                Folder:{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {mailboxConfig.folder}
                </span>
              </p>
              {mailboxConfig.rules.from_senders.length > 0 && (
                <p>
                  From senders:{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {mailboxConfig.rules.from_senders.join(', ')}
                  </span>
                </p>
              )}
              {mailboxConfig.rules.subject_contains && (
                <p>
                  Subject contains:{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {mailboxConfig.rules.subject_contains}
                  </span>
                </p>
              )}
              <p>
                Attachments only:{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {mailboxConfig.rules.attachments_only ? 'Yes' : 'No'}
                </span>
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <XCircle className="h-4 w-4 text-gray-400" />
            <span className="text-gray-400">Skipped</span>
          </div>
        )}
      </SectionCard>

      {/* 4. Vendor Fields */}
      <SectionCard
        title="Vendor Fields"
        stepId="vendor_fields"
        onEdit={onEditStep}
      >
        <FieldPills keys={vendorFields} allFields={VENDOR_FIELDS} />
      </SectionCard>

      {/* 5. Bill Creation Fields */}
      <SectionCard
        title="Bill Creation Fields"
        stepId="bill_creation_fields"
        onEdit={onEditStep}
      >
        <FieldPills keys={billFields} allFields={BILL_CREATION_FIELDS} />
      </SectionCard>
    </div>
  );
};
